import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import { serverDomain } from "../../config";
import { useNavigate } from "react-router-dom";
import "../../styles/ReferralsPage.css";

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
};

function ReferralsPage() {
  const [refLink, setRefLink] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [activeTab, setActiveTab] = useState("statistics");
  const [statistics, setStatistics] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");

  const navigate = useNavigate();

  // дефолт: последние 7 дней
  useEffect(() => {
    const today = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 7);
    const fmt = (d) => d.toISOString().split("T")[0];
    setStartDate(fmt(lastWeek));
    setEndDate(fmt(today));
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `${serverDomain}/api/referrals/statistics/referrals?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error("Ошибка загрузки статистики");
      const data = await response.json();
      setStatistics(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Ошибка при загрузке статистики:", e);
      setStatistics([]);
    } finally {
      setLoading(false);
    }
  };

  // авто-обновление при смене дат
  useEffect(() => {
    if (startDate && endDate) fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // загрузка ссылки + списка рефералов
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        const [linkRes, refsRes] = await Promise.all([
          fetch(`${serverDomain}/api/referrals/referral-link`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${serverDomain}/api/referrals/referrals`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!linkRes.ok) throw new Error("Ошибка загрузки реферальной ссылки");
        if (!refsRes.ok) throw new Error("Ошибка загрузки рефералов");

        const linkData = await linkRes.json();
        const refsData = await refsRes.json();

        setReferrals(Array.isArray(refsData) ? refsData : []);

        const origin = window.location.origin || "https://fl-dating.su";
        const code = linkData?.link || "XXXXXX";
        setRefLink(`${origin}/?ref=${encodeURIComponent(code)}`);
      } catch (e) {
        console.error("Ошибка при загрузке данных рефералов:", e);
        setReferrals([]);
        setRefLink("");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [navigate]);

  const totalCommission = useMemo(() => {
    return (statistics || []).reduce((sum, row) => sum + (Number(row.commission) || 0), 0);
  }, [statistics]);

  const totalReferralsCommission = useMemo(() => {
    return (referrals || []).reduce((sum, r) => sum + Number(r.total_commission || 0), 0);
  }, [referrals]);

  const handleCopyLink = async () => {
    if (!refLink) return;
    const ok = await copyToClipboard(refLink);
    setToast(ok ? "Ссылка скопирована" : "Не удалось скопировать");
    setTimeout(() => setToast(""), 1200);
  };

  return (
    <div>
      <TopBar pageTitle={"Рефералы"} />
      <div className="main-container">
        <SideBar />

        <main className="home-main flex-grow-1">
          <div className="fl-page fl-referrals">
            {/* Карточка с реф-ссылкой */}
            <section className="fl-ref-card">
              <div className="fl-ref-card__top">
                <div className="fl-ref-card__title">Партнёрская программа</div>
                <div className="fl-ref-card__hint">
                  Вы будете зарабатывать <b>10%</b> со всех доходов ваших партнёров.
                </div>
              </div>

              <div className="fl-ref-linkrow">
                <div className="fl-ref-linklabel">Реф. ссылка</div>

                <div className="fl-ref-linkwrap">
                  <div className="fl-ref-link fl-ellipsis" title={refLink}>
                    {refLink || "—"}
                  </div>

                  <button className="fl-iconbtn fl-iconbtn--copy" type="button" onClick={handleCopyLink} title="Скопировать">
                    📋
                  </button>
                </div>
              </div>
            </section>

            {/* Tabs */}
            <div className="fl-subtabs">
              <button
                className={`fl-subtabs__btn ${activeTab === "statistics" ? "is-active" : ""}`}
                onClick={() => setActiveTab("statistics")}
                type="button"
              >
                Статистика
              </button>
              <button
                className={`fl-subtabs__btn ${activeTab === "referrals" ? "is-active" : ""}`}
                onClick={() => setActiveTab("referrals")}
                type="button"
              >
                Рефералы
              </button>
            </div>

            {/* TAB: Statistics */}
            {activeTab === "statistics" && (
              <section className="fl-cardbox">
                <div className="fl-toolbar fl-ref-toolbar">
                  <input
                    className="fl-input"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <span className="fl-toolbar__dash">-</span>
                  <input
                    className="fl-input"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />

                  <button className="fl-btn" type="button" onClick={fetchStatistics} disabled={loading}>
                    Обновить
                  </button>

                  <div className="fl-ref-total">
                    Итого за период: <b>{totalCommission.toFixed(2)} ₽</b>
                  </div>
                </div>

                {loading ? (
                  <div className="loading-container">
                    <div className="loader" />
                  </div>
                ) : statistics.length === 0 ? (
                  <div className="fl-empty">Нет данных</div>
                ) : (
                  <div className="fl-table-wrap">
                    <table className="fl-table">
                      <thead>
                        <tr>
                          <th>День</th>
                          <th>Почта реферала</th>
                          <th>Отчисления (10%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statistics.map((item, idx) => (
                          <tr key={idx}>
                            <td>{new Date(item.day).toLocaleDateString("ru-RU")}</td>
                            <td className="fl-ellipsis" title={item.email}>{item.email}</td>
                            <td>{Number(item.commission).toFixed(2)} ₽</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td style={{ fontWeight: 1000 }}>Итого</td>
                          <td />
                          <td style={{ fontWeight: 1000 }}>{totalCommission.toFixed(2)} ₽</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </section>
            )}

            {/* TAB: Referrals */}
            {activeTab === "referrals" && (
              <section className="fl-cardbox">
                <div className="fl-ref-head">
                  <div className="fl-ref-head__title">Список рефералов</div>
                  <div className="fl-ref-head__meta">
                    Итого за всё время: <b>{totalReferralsCommission.toFixed(2)} ₽</b>
                  </div>
                </div>

                {loading ? (
                  <div className="loading-container">
                    <div className="loader" />
                  </div>
                ) : referrals.length === 0 ? (
                  <div className="fl-empty">Нет данных</div>
                ) : (
                  <div className="fl-table-wrap">
                    <table className="fl-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Реферал</th>
                          <th className="fl-hide-sm">Дата создания</th>
                          <th>Отчисления</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referrals.map((r) => (
                          <tr key={r.id}>
                            <td>{r.id}</td>
                            <td className="fl-ellipsis" title={r.email}>{r.email}</td>
                            <td className="fl-hide-sm">{new Date(r.created_at).toLocaleString("ru-RU")}</td>
                            <td>{Number(r.total_commission || 0).toFixed(2)} ₽</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td style={{ fontWeight: 1000 }}>Итого</td>
                          <td />
                          <td className="fl-hide-sm" />
                          <td style={{ fontWeight: 1000 }}>{totalReferralsCommission.toFixed(2)} ₽</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </section>
            )}

            {toast ? <div className="fl-toast">{toast}</div> : null}
          </div>
        </main>
      </div>
    </div>
  );
}

export default ReferralsPage;